'use client';

import React, { useState } from 'react';
import { User } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { TurnstileWidget } from '@/components/ui/TurnstileWidget';
import { useChatStore, GenderType } from '@/store/chatStore';

interface GuestSetupModalProps {
  onConfirm: (name: string, gender: GenderType, turnstileToken: string | null) => void;
}

export function GuestSetupModal({ onConfirm }: GuestSetupModalProps) {
  const { guestDisplayName, myGender } = useChatStore();
  const [name, setName] = useState(guestDisplayName || '');
  const [gender, setGender] = useState<GenderType | null>(myGender);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const siteKeyConfigured = !!process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  const handleSubmit = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Please enter a display name');
      return;
    }
    if (trimmed.length < 2) {
      setError('Name must be at least 2 characters');
      return;
    }
    if (!gender) {
      setError('Please select your gender');
      return;
    }
    if (siteKeyConfigured && !turnstileToken) {
      setError('Please complete the security check');
      return;
    }
    onConfirm(trimmed, gender, turnstileToken);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-sm bg-brand-surface border border-white/10 rounded-2xl p-6 shadow-2xl animate-slide-up">
        {/* Header */}
        <div className="flex flex-col items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-full bg-brand-lime/10 border border-brand-lime/30 flex items-center justify-center">
            <User size={22} className="text-brand-lime" />
          </div>
          <div className="text-center">
            <h2 className="text-lg font-semibold text-brand-white">Before you start</h2>
            <p className="text-sm text-brand-gray mt-1">
              Set your name and gender so strangers know who they&apos;re talking to
            </p>
          </div>
        </div>

        {/* Name field */}
        <div className="mb-4">
          <label className="block text-xs font-medium text-brand-gray-light mb-2">
            Display Name
          </label>
          <Input
            placeholder="e.g. CoolFox, Midnight, Alex..."
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError('');
            }}
            onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            maxLength={24}
            autoFocus
          />
          <p className="text-xs text-brand-gray mt-1">
            {name.length}/24 · This name will be shown to the stranger
          </p>
        </div>

        {/* Gender selector */}
        <div className="mb-4">
          <label className="block text-xs font-medium text-brand-gray-light mb-2">
            I am
          </label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => { setGender('MALE'); setError(''); }}
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                gender === 'MALE'
                  ? 'bg-blue-500/20 border-blue-500/60 text-blue-400'
                  : 'border-white/10 text-brand-gray-light hover:border-white/20 hover:text-brand-white'
              }`}
            >
              <span className="font-bold text-xs">M</span>
              Male
            </button>
            <button
              type="button"
              onClick={() => { setGender('FEMALE'); setError(''); }}
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                gender === 'FEMALE'
                  ? 'bg-pink-500/20 border-pink-500/60 text-pink-400'
                  : 'border-white/10 text-brand-gray-light hover:border-white/20 hover:text-brand-white'
              }`}
            >
              <span className="font-bold text-xs">F</span>
              Female
            </button>
          </div>
        </div>

        {/* Turnstile */}
        {siteKeyConfigured && (
          <div className="mb-4 flex justify-center">
            <TurnstileWidget
              onVerify={(token) => { setTurnstileToken(token); setError(''); }}
              onExpire={() => setTurnstileToken(null)}
            />
          </div>
        )}

        {error && (
          <p className="text-xs text-brand-red mb-4">{error}</p>
        )}

        <Button fullWidth onClick={handleSubmit}>
          Start Chatting
        </Button>
      </div>
    </div>
  );
}
